import { useEffect, useRef, type RefObject } from 'react';

/**
 * A surface that opens takes focus, and gives it back when it closes.
 *
 * Nothing in the web client did either. `grep "tabIndex\|.focus()\|autoFocus"` over the task
 * list, the panel, the bulk editor, the keys sheet and the menu surface came back empty, so the
 * systems architect's round on 1.82.0 found the panel open with focus still in the list — forty
 * Tabs through every row before the panel's first control — and, after Escape, focus on `<body>`,
 * where the next Tab starts at the top nav and the `j`/`k` cursor and the DOM focus disagree.
 *
 * One hook for every surface, because the panel was one report and the bulk *Edit…* dialog and the
 * `?` sheet were another with the same root. It does the two halves every one of them needs and
 * nothing a particular kind needs: a dialog that traps Tab layers that on top, a panel beside a
 * list does not and must not.
 *
 * **On open** it focuses `initial()`, or the surface itself when that is not given or returns
 * null — so the surface carries `tabIndex={-1}`, which makes it focusable by script and leaves it
 * out of the Tab order. It stands down if focus is already inside, so a control that focuses
 * itself on mount (a filter field, say) keeps it.
 *
 * **On close** — `open` going false, or the component unmounting — it focuses `returnTo()`, which
 * is asked *then*, not at open, or else whatever had focus when the surface opened. Asked late on
 * purpose: at 375 the full task view replaces the list, so the row that opened it is unmounted
 * while it is up, and the row that comes back is a new element. A remembered element would be a
 * detached node and focusing it does nothing at all.
 *
 * **`trap`** keeps Tab inside while the surface is open — for the bulk *Edit…* dialog and the `?`
 * sheet, never for the panel. It is a layer on the same hook rather than a second one, because a
 * trap is only ever wanted on a surface that also takes focus and gives it back, and two hooks
 * meant two effects that could disagree about when the surface opened. See `trapTab`.
 *
 * It only hands focus back if nobody else has taken it. Closing because a nav tab was clicked
 * leaves focus on the tab; closing because Escape was pressed leaves it nowhere — the surface it
 * was in is gone, so it is on `<body>` — and that is the case this is for. The check runs a frame
 * later, after the commit that closed the surface, so a list remounting in that same commit is
 * there to be found, and a surface opening in its place (⤢ from the panel to the full view) has
 * already taken focus and is left alone.
 *
 * **And not after a pointer press outside the surface**, which also leaves focus on `<body>`.
 * Clicking a row's body while *Edit…* was open closed the dialog through `useDismiss`, the row
 * cannot take focus, so focus fell to `<body>` — indistinguishable, by where focus is, from
 * Escape. The hook sent it back to *Edit…*, the row's mousedown had meanwhile moved the cursor to
 * the clicked row, and the next Space met `focusOwnsKey` on a bar button and reopened the dialog
 * instead of acting on that row (review of MR !106). A person who clicked somewhere has said where
 * they are; Escape says only "not this any more". So the hook watches, while the surface is open,
 * which input came last: a `pointerdown` outside the surface marks the close as the pointer's, a
 * `pointerdown` inside or any `keydown` clears the mark. Apply or ✕ pressed with the mouse is a
 * press *inside*, so it still hands focus back, as does Escape after an earlier stray click.
 * Captured on `document` so it is read before `useDismiss`'s own `pointerdown` closes the surface.
 * Of the four users only the bulk dialog closes on an outside press today. The task panel and the
 * `?` sheet stay up under a click on a row's body (measured at 1280: surface still open, focus on
 * `<body>`), and the full view at 375 covers the list, leaving only the nav, which takes focus
 * itself (inferred, not measured). For those three the rule is latent; it lives in the hook so that
 * giving one of them an outside dismissal cannot bring this back.
 */
export function useFocusOnOpen(
  open: boolean,
  surface: RefObject<HTMLElement | null>,
  opts: {
    /**
     * What to focus on open. Defaults to the surface — or, with `trap`, to its first stop, which is
     * where a dialog puts you; a trapped surface with nothing to Tab to takes focus itself.
     */
    initial?: () => HTMLElement | null;
    /**
     * What to focus on close, resolved at close. Handed the opener — whatever had focus at open, if
     * it is still in the document and was not `<body>` — and falls back to it on null.
     */
    returnTo?: (opener: HTMLElement | null) => HTMLElement | null;
    /** Keep Tab and Shift+Tab inside the surface while it is open. See `trapTab`. */
    trap?: boolean;
  } = {},
): void {
  // Read through a ref so a caller's inline closures do not re-run the open/close effect, which
  // would bounce focus out and back on every render.
  const latest = useRef(opts);
  latest.current = opts;
  const opener = useRef<HTMLElement | null>(null);
  /*
    The pending hand-back, so an effect that runs again before it fires can cancel it.

    React's StrictMode mounts, cleans up and mounts again in development, and without this the
    first cleanup's frame landed after the second mount had focused the surface — saw focus inside
    it, took that for "closing with focus in here", and sent it back to the row. The panel opened
    and immediately gave its focus away. Any real re-run inside one frame has the same shape.
  */
  const pending = useRef<number | null>(null);
  /** The last input while open was a pointer press outside the surface. See the docblock. */
  const pointerOutside = useRef(false);

  useEffect(() => {
    if (!open) return;
    if (pending.current !== null) cancelAnimationFrame(pending.current);
    pending.current = null;
    const el = surface.current;
    const active = document.activeElement;
    // Only what was focused *outside* is an opener; a re-run finds focus already in here.
    if (!el?.contains(active)) {
      // `<body>` is nobody: a surface opened by a key with nothing focused has no opener.
      opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
      const first = latest.current.trap && el ? (tabbables(el)[0] ?? null) : null;
      (latest.current.initial?.() ?? first ?? el)?.focus({ preventScroll: true });
    }
    pointerOutside.current = false;
    const onPointer = (event: PointerEvent) => {
      pointerOutside.current = !(surface.current?.contains(event.target as Node) ?? false);
    };
    const onKey = () => {
      pointerOutside.current = false;
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey, true);
      pending.current = requestAnimationFrame(() => {
        pending.current = null;
        // The pointer put the person somewhere; focusing the opener would overrule them.
        if (pointerOutside.current) return;
        const now = document.activeElement;
        const unclaimed =
          now === null ||
          now === document.body ||
          !now.isConnected ||
          (surface.current?.contains(now) ?? false);
        if (!unclaimed) return;
        const back = opener.current?.isConnected ? opener.current : null;
        (latest.current.returnTo?.(back) ?? back)?.focus();
      });
    };
  }, [open, surface]);

  const trap = opts.trap ?? false;
  useEffect(() => {
    const el = surface.current;
    if (!open || !trap || !el) return;
    const onKey = (event: KeyboardEvent) => trapTab(event, el);
    el.addEventListener('keydown', onKey);
    return () => el.removeEventListener('keydown', onKey);
  }, [open, trap, surface]);
}

/**
 * What Tab can land on, in document order, among what is actually drawn.
 *
 * Drawn, not merely present: `Select` keeps its listbox in the DOM behind `hidden`, and a closed
 * option counted as the last stop would make Tab from Apply go nowhere visible.
 */
const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

function tabbables(surface: HTMLElement): HTMLElement[] {
  return Array.from(surface.querySelectorAll<HTMLElement>(TABBABLE)).filter(
    (el) => el.getClientRects().length > 0 && el.closest('[hidden], [inert]') === null,
  );
}

/**
 * Tab from the last stop goes to the first, Shift+Tab from the first goes to the last.
 *
 * Only the two ends are handled, and the browser walks everything between — so a date input's
 * month, day and year segments, which Chrome steps through on Tab, keep doing that. Moving focus by
 * hand on every press would skip them. The surface itself (focused on open, `tabIndex={-1}`) counts
 * as before the first stop, and a surface with nothing to Tab to keeps focus where it is: the `?`
 * sheet is text, and Tab leaving it is the defect this exists for.
 *
 * Bound on the surface, not `document`, so it acts only while focus is inside. A pointer that puts
 * focus outside — which closes the bulk dialog and leaves the sheet up — is not overruled; the
 * dialog is a popover over a live list, not a modal, and does not claim `aria-modal`.
 */
function trapTab(event: KeyboardEvent, surface: HTMLElement): void {
  if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
  const stops = tabbables(surface);
  const active = document.activeElement;
  if (stops.length === 0) {
    event.preventDefault();
    return;
  }
  const first = stops[0]!;
  const last = stops[stops.length - 1]!;
  if (event.shiftKey ? active === first || active === surface : active === last) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  } else if (active === surface) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * The control a task's row or card offers to open it, by task id.
 *
 * The return target for the task panel and the full view: the row's `.task-title` button on the
 * list, the card's `.board-card-title` on the board. Those are the elements `focusOwnsKey` leaves
 * to the list, so with focus back on one, Enter still opens the cursor task and `j`/`k`/`x` still
 * act on it. Null on a screen that drew no such row — Today, Week, Goals — and the hook falls back
 * to whatever opened the task, which on those screens stays mounted.
 */
export function taskOpener(taskId: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(
    `[data-task-id="${CSS.escape(taskId)}"] :is(.task-title, .board-card-title)`,
  );
}
